import React from "react";
import { FullCalendarGlobalStyle } from "./styles";
import FullCalendar, {
  EventClickArg,
  EventContentArg,
} from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import list from "@fullcalendar/list";
import { Empty, Spin } from "antd";
import moment from "moment";

export type CalendarEventInput = Record<string, any> & {
  title: string;
  start: string;
  end?: string;
  backgroundColor?: string;
  allDay?: boolean;
  id?: string;
};

interface CalendarProps {
  events: CalendarEventInput[];
  handleEventClick: (event: EventClickArg) => void;
  renderEventContent: (event: EventContentArg) => React.ReactNode;
  onChangeDate: (dateStart: string, dateEnd: string) => void;
  setLocalView: (view: string) => void;
  dateClick?: (date: string) => void;
  hideOptionsView?: boolean;
  noEventsText?: string;
  displayEventTime?: boolean;
  loading?: boolean;
  centerTitle?: boolean;
  height?: string | number;
  contentHeight?: string | number;
}

const Calendar = React.forwardRef<FullCalendar | null, CalendarProps>(
  (props, ref) => {
    const {
      events,
      handleEventClick,
      dateClick,
      renderEventContent,
      onChangeDate,
      setLocalView,
      hideOptionsView,
      noEventsText = "Sem eventos para esse mês",
      displayEventTime = true,
      loading = false,
      centerTitle = false,
      height,
      contentHeight = "auto",
    } = props;

    const tz = moment().zoneName();

    const renderNoEventsContent = (): React.ReactElement => {
      if (loading) {
        return <Spin size="large" />;
      }
      return <Empty description={noEventsText} />;
    };

    return (
      <>
        <FullCalendarGlobalStyle />
        <FullCalendar
          ref={ref}
          height={height}
          contentHeight={contentHeight}
          plugins={[dayGridPlugin, list, interactionPlugin]}
          editable={true}
          displayEventTime={displayEventTime}
          headerToolbar={{
            left: centerTitle ? undefined : "prev title next",
            right: hideOptionsView ? "" : "listMonth dayGridMonth",
            center: centerTitle ? "prev title next" : undefined,
          }}
          timeZone={tz}
          viewDidMount={({ view }) => setLocalView(view.type)}
          datesSet={({ startStr, endStr }) => {
            onChangeDate(
              moment(startStr).format("YYYY/MM/DD"),
              moment(endStr).subtract(1, "days").format("YYYY/MM/DD")
            );
          }}
          allDayText="O Dia Todo"
          allDayMaintainDuration={true}
          initialView="dayGridMonth"
          noEventsContent={renderNoEventsContent}
          noEventsText={noEventsText}
          events={events as never}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          locale="pt-br"
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
          }}
          buttonText={{
            today: "Hoje",
            month: "Mês",
            week: "Semana",
            day: "Diário",
            list: "Linha",
          }}
          eventClick={handleEventClick}
          defaultTimedEventDuration="01:00"
          eventContent={renderEventContent}
          dragScroll={false}
          eventStartEditable={false}
          selectAllow={() => false}
          dateClick={(d) => dateClick?.(d.dateStr)}
        />
      </>
    );
  }
);

export default Calendar;
